import React, { useState, useRef, useEffect } from 'react';

interface Row {
  date: string;
  time: string | number;
  price: string | number;
  pricingType?: string;
  service?: string;
}

interface ModalGenerateReviewProps {
  freelancerOptions: Array<{ value: string; label: string }>;
  target: string; // which freelancer(s) receive the new rows ('all' or a uid)
  existingRowsByFreelancer: { [uid: string]: Row[] };
  pensionData: { [uid: string]: { pension: boolean; pensionAmount: number } };
  holidayData: { [uid: string]: { holidaySavings: boolean; holidaySavingsFee: number } };
  selectedDates: Date[];
  formData: { hoursPerDay: string; hourlyPrice: string; pricingType: string; serviceDescription: string };
  currency: string;
  t: (key: string) => string;
}

const fmtStored = (d: string): string => {
  if (!d) return '';
  const parts = d.split(/[/-]/).map((p) => parseInt(p, 10));
  if (parts.length === 3) {
    const [mm, dd, yyyy] = parts;
    const dt = new Date(yyyy, (mm || 1) - 1, dd || 1);
    if (!isNaN(dt.getTime())) return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return d;
};

const fmtDate = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const rowTotal = (time: string | number, price: string | number): number => {
  const p = parseFloat(String(price || '0')) || 0;
  const ts = String(time || '');
  if (ts.includes(':')) {
    const [h, m] = ts.split(':').map(Number);
    return ((h || 0) + (m || 0) / 60) * p;
  }
  return (parseFloat(ts || '1') || 1) * p;
};

const ModalGenerateReview: React.FC<ModalGenerateReviewProps> = ({
  freelancerOptions,
  target,
  existingRowsByFreelancer,
  pensionData,
  holidayData,
  selectedDates,
  formData,
  currency,
  t,
}) => {
  const realFreelancers = freelancerOptions.filter((o) => o.value !== 'all');
  const [viewTarget, setViewTarget] = useState<string>(target === 'all' ? 'all' : target);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expanded, setExpanded] = useState<{ [uid: string]: boolean }>({});
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setViewTarget(target === 'all' ? 'all' : target);
  }, [target]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const nameOf = (uid: string) => realFreelancers.find((o) => o.value === uid)?.label || uid;
  const willReceive = (uid: string) => target === 'all' || target === uid;

  const renderDetail = (uid: string) => {
    const existing = existingRowsByFreelancer[uid] || [];
    const pension = pensionData[uid];
    const holiday = holidayData[uid];
    const receives = willReceive(uid);

    return (
      <div className="ag-rv-detail">
        {/* Existing working hours */}
        <div className="ag-rv-block">
          <div className="ag-rv-block-title">
            {t('EXISTING_WORKING_HOURS') || 'Existing working hours'}
            <span className="ag-rv-count">{existing.length}</span>
          </div>
          {existing.length === 0 ? (
            <div className="ag-rv-empty">{t('NO_WORKING_HOURS_YET') || 'No working hours added yet.'}</div>
          ) : (
            <div className="ag-rv-rows">
              {existing.map((r, i) => (
                <div className="ag-rv-row" key={i}>
                  <span className="ag-rv-row-date">{fmtStored(r.date)}</span>
                  <span className="ag-rv-row-meta">
                    {r.time} · {r.price} {currency}
                  </span>
                  <span className="ag-rv-row-total">
                    {rowTotal(r.time, r.price).toFixed(2)} {currency}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pension & Holiday status */}
        <div className="ag-rv-tags">
          <span className={`ag-rv-tag${pension?.pension ? ' on' : ''}`}>
            {t('PENSION') || 'Pension'}:{' '}
            {pension?.pension ? `${pension.pensionAmount} ${currency}` : t('NOT_ADDED') || 'Not added'}
          </span>
          <span className={`ag-rv-tag${holiday?.holidaySavings ? ' on' : ''}`}>
            {t('HOLIDAY_PAY') || 'Holiday Pay'}:{' '}
            {holiday?.holidaySavings ? `${holiday.holidaySavingsFee}%` : t('NOT_ADDED') || 'Not added'}
          </span>
        </div>

        {/* New rows about to be added */}
        {receives && selectedDates.length > 0 && (
          <div className="ag-rv-block ag-rv-new">
            <div className="ag-rv-block-title">
              {t('WILL_BE_ADDED') || 'Will be added now'}
              <span className="ag-rv-count is-new">{selectedDates.length}</span>
            </div>
            <div className="ag-rv-rows">
              {[...selectedDates]
                .sort((a, b) => a.getTime() - b.getTime())
                .map((d, i) => (
                  <div className="ag-rv-row" key={i}>
                    <span className="ag-rv-row-date">{fmtDate(d)}</span>
                    <span className="ag-rv-row-meta">
                      {formData.hoursPerDay} · {formData.hourlyPrice || 0} {currency}
                    </span>
                    <span className="ag-rv-row-total">
                      {rowTotal(formData.hoursPerDay, formData.hourlyPrice).toFixed(2)} {currency}
                    </span>
                  </div>
                ))}
            </div>
            {formData.serviceDescription ? (
              <div className="ag-rv-service">
                {t('SERVICE_DESCRIPTION') || 'Service'}: <strong>{formData.serviceDescription}</strong>
              </div>
            ) : null}
          </div>
        )}
        {receives && selectedDates.length === 0 && (
          <div className="ag-rv-empty">{t('NO_NEW_DATES_SELECTED') || 'No new dates selected to add.'}</div>
        )}
      </div>
    );
  };

  return (
    <div className="ag-review">
      <div className="ag-review-head">
        <label className="ag-review-label">{t('REVIEW_FREELANCER') || 'View details for'}</label>
        <div className="ag-freelancer-select ag-review-select" ref={dropRef} style={{ position: 'relative' }}>
          <div className={`ag-fs-trigger${dropdownOpen ? ' open' : ''}`} onClick={() => setDropdownOpen((o) => !o)}>
            <span>{viewTarget === 'all' ? t('ALL') || 'All' : nameOf(viewTarget)}</span>
            <svg className={`ag-fs-arrow${dropdownOpen ? ' open' : ''}`} width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          {dropdownOpen && (
            <div className="ag-fs-list">
              {freelancerOptions.map((opt) => (
                <div
                  key={opt.value}
                  className={`ag-fs-item${viewTarget === opt.value ? ' selected' : ''}`}
                  onClick={() => {
                    setViewTarget(opt.value);
                    setDropdownOpen(false);
                  }}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {viewTarget === 'all' ? (
        <div className="ag-rv-cards">
          {realFreelancers.map((f) => {
            const uid = f.value;
            const existing = existingRowsByFreelancer[uid] || [];
            const isOpen = !!expanded[uid];
            return (
              <div className={`ag-rv-card${isOpen ? ' open' : ''}`} key={uid}>
                <button
                  type="button"
                  className="ag-rv-card-head"
                  onClick={() => setExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }))}
                >
                  <span className="ag-rv-card-name">{f.label}</span>
                  <span className="ag-rv-card-summary">
                    {existing.length} {t('EXISTING') || 'existing'}
                    {willReceive(uid) && selectedDates.length > 0 ? ` · +${selectedDates.length} ${t('NEW') || 'new'}` : ''}
                  </span>
                  <svg className="ag-rv-card-chevron" width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {isOpen && renderDetail(uid)}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="ag-rv-single">{renderDetail(viewTarget)}</div>
      )}
    </div>
  );
};

export default ModalGenerateReview;
